import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2 } from "lucide-react"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { DatePopover } from "@/components/date-popover"
import { Switch } from "@/components/ui/switch"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  CALCULATION_METHOD_VALUES,
  CERTIFICATE_VALUES,
  COCOA_GRADE_VALUES,
  CONTRACT_TYPE_VALUES,
  CURRENCY_VALUES,
  INCOTERM_VALUES,
  MONTH_VALUES,
  ORIGIN_DEFAULT,
  PRICING_MECHANISM_VALUES,
  PRODUCT_TYPE_VALUES,
  REFERENCE_INDEX_VALUES,
  REFERENCE_MARKET_VALUES,
  REFERENCE_YEARS,
} from "@/features/contracts/components/contract-constants"
import type { ContractWriteRequest } from "@/features/contracts/api/contracts.api"
import type { Contract } from "@/types/domain"

const NONE = "__none__"

function RequiredMark() {
  return <span className="ml-0.5 text-destructive">*</span>
}

function currencySymbol(currency?: string | null) {
  switch (currency) {
    case "EUR":
      return "€"
    case "USD":
      return "$"
    case "GBP":
      return "£"
    case "CFA":
      return "CFA"
    default:
      return ""
  }
}

function formatMoney(value: number) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const numberOrNull = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === "" || v === null || v === undefined) return null
    const n = typeof v === "number" ? v : Number(v)
    return Number.isNaN(n) ? null : n
  })

const stringOrNull = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((v) => (v === undefined || v === null || v === "" ? null : v))

const yearOrNull = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === "" || v === null || v === undefined || v === NONE) return null
    const n = typeof v === "number" ? v : Number(v)
    return Number.isNaN(n) ? null : n
  })

const contractFormSchema = z
  .object({
    contractNumber: stringOrNull,
    contractDate: stringOrNull,
    contractType: stringOrNull,
    clientName: z.string().min(1),
    productType: stringOrNull,
    cocoaGrade: stringOrNull,
    quantity: z
      .union([z.string(), z.number()])
      .transform((v) => Number(v))
      .refine((n) => !Number.isNaN(n) && n > 0),
    origin: stringOrNull,
    basePrice: numberOrNull,
    currency: z.string().min(1),
    paymentTerms: stringOrNull,
    incoterms: stringOrNull,
    notes: stringOrNull,
    adjustments: stringOrNull,
    portOfLoading: stringOrNull,
    destinationPort: stringOrNull,
    differential: numberOrNull,
    countryDifferential: numberOrNull,
    pricingMechanism: z.string().min(1),
    referenceMarket: stringOrNull,
    calculationMethod: stringOrNull,
    floatingDiscount: numberOrNull,
    fixingStartDate: stringOrNull,
    fixingEndDate: stringOrNull,
    referenceIndex: stringOrNull,
    referenceMonth: stringOrNull,
    referenceYear: yearOrNull,
    contractDurationStart: stringOrNull,
    contractDurationEnd: stringOrNull,
    agroforestryBonusPerKilo: numberOrNull,
    certificationPremiumPerKilo: z
      .union([z.string(), z.number()])
      .transform((v) => Number(v))
      .refine((n) => !Number.isNaN(n) && n >= 0),
    fairtradePremiumPerKilo: numberOrNull,
    localAgroforestryBonusPerKilo: numberOrNull,
    localCertificationPremiumPerKilo: z
      .union([z.string(), z.number()])
      .transform((v) => Number(v))
      .refine((n) => !Number.isNaN(n) && n >= 0),
    organicPremiumPerKilo: numberOrNull,
    rainforestPremiumPerKilo: numberOrNull,
    utzPremiumPerKilo: numberOrNull,
    certificates: z.array(z.string()),
    onlyAcceptsEUDR: z.boolean(),
    onlyAcceptsCertificates: z.array(z.string()),
    onlyAcceptNoChildLabor: z.boolean(),
  })
  .superRefine((values, ctx) => {
    const otherMain = values.agroforestryBonusPerKilo
    const otherLocal = values.localAgroforestryBonusPerKilo
    if (otherMain != null && otherLocal == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localAgroforestryBonusPerKilo"],
        message: "required",
      })
    }
    if (otherLocal != null && otherMain == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["agroforestryBonusPerKilo"],
        message: "required",
      })
    }
  })

type ContractFormInput = z.input<typeof contractFormSchema>
type ContractFormOutput = z.output<typeof contractFormSchema>

function defaultsFromContract(contract?: Contract | null): ContractFormInput {
  return {
    contractNumber: contract?.contractNumber ?? "",
    contractDate: contract?.contractDate ?? "",
    contractType: contract?.contractType ?? "SPOT_CONTRACTS",
    clientName: contract?.clientName ?? "",
    productType: contract?.productType ?? "COCOA_BEANS",
    cocoaGrade: contract?.cocoaGrade ?? "",
    quantity: (contract?.quantity ?? "") as ContractFormInput["quantity"],
    origin: contract?.origin ?? ORIGIN_DEFAULT,
    basePrice: (contract?.basePrice ?? "") as ContractFormInput["basePrice"],
    currency: contract?.currency ?? "EUR",
    paymentTerms: contract?.paymentTerms ?? "",
    incoterms: contract?.incoterms ?? "",
    notes: contract?.notes ?? "",
    adjustments:
      typeof contract?.adjustments === "string" ? contract.adjustments : "",
    portOfLoading: contract?.portOfLoading ?? "",
    destinationPort: contract?.destinationPort ?? "",
    differential: (contract?.differential ?? "") as ContractFormInput["differential"],
    countryDifferential: (contract?.countryDifferential ?? "") as ContractFormInput["countryDifferential"],
    pricingMechanism: contract?.pricingMechanism ?? "",
    referenceMarket: contract?.referenceMarket ?? "",
    calculationMethod: contract?.calculationMethod ?? "",
    floatingDiscount: (contract?.floatingDiscount ?? "") as ContractFormInput["floatingDiscount"],
    fixingStartDate: contract?.fixingStartDate ?? "",
    fixingEndDate: contract?.fixingEndDate ?? "",
    referenceIndex: contract?.referenceIndex ?? "",
    referenceMonth: contract?.referenceMonth ?? "",
    referenceYear: (contract?.referenceYear ?? "") as ContractFormInput["referenceYear"],
    contractDurationStart: contract?.contractDurationStart ?? "",
    contractDurationEnd: contract?.contractDurationEnd ?? "",
    agroforestryBonusPerKilo: (contract?.agroforestryBonusPerKilo ?? "") as ContractFormInput["agroforestryBonusPerKilo"],
    certificationPremiumPerKilo: (contract?.certificationPremiumPerKilo ?? "") as ContractFormInput["certificationPremiumPerKilo"],
    fairtradePremiumPerKilo: (contract?.fairtradePremiumPerKilo ?? "") as ContractFormInput["fairtradePremiumPerKilo"],
    localAgroforestryBonusPerKilo: (contract?.localAgroforestryBonusPerKilo ?? "") as ContractFormInput["localAgroforestryBonusPerKilo"],
    localCertificationPremiumPerKilo: (contract?.localCertificationPremiumPerKilo ?? "") as ContractFormInput["localCertificationPremiumPerKilo"],
    organicPremiumPerKilo: (contract?.organicPremiumPerKilo ?? "") as ContractFormInput["organicPremiumPerKilo"],
    rainforestPremiumPerKilo: (contract?.rainforestPremiumPerKilo ?? "") as ContractFormInput["rainforestPremiumPerKilo"],
    utzPremiumPerKilo: (contract?.utzPremiumPerKilo ?? "") as ContractFormInput["utzPremiumPerKilo"],
    certificates: contract?.certificates ?? [],
    onlyAcceptsEUDR: contract?.onlyAcceptsEUDR ?? true,
    onlyAcceptsCertificates: contract?.onlyAcceptsCertificates ?? [],
    onlyAcceptNoChildLabor: contract?.onlyAcceptNoChildLabor ?? false,
  }
}

interface ContractFormProps {
  contract?: Contract | null
  isSubmitting: boolean
  onSubmit: (payload: ContractWriteRequest) => void
}

export function ContractForm({ contract, isSubmitting, onSubmit }: ContractFormProps) {
  const { t } = useTranslation(["contracts", "common"])
  const navigate = useNavigate()

  const defaults = useMemo(() => defaultsFromContract(contract), [contract])
  const form = useForm<ContractFormInput, unknown, ContractFormOutput>({
    resolver: zodResolver(contractFormSchema),
    defaultValues: defaults,
  })

  useEffect(() => {
    form.reset(defaults)
  }, [defaults, form])

  const pricingMechanism = form.watch("pricingMechanism")
  const showFixedPrice = pricingMechanism === "FIXED_PRICE"
  const showDifferential = pricingMechanism === "DIFFERENTIAL_PRICE"
  const showFloating = pricingMechanism === "FLOATING"
  const showPtbf = pricingMechanism === "MARKET_INDEXED"

  const summaryWatch = form.watch([
    "currency",
    "quantity",
    "basePrice",
    "differential",
    "floatingDiscount",
    "referenceMarket",
    "referenceMonth",
    "referenceYear",
    "referenceIndex",
  ])
  const [
    summaryCurrency,
    summaryQuantity,
    summaryBasePrice,
    summaryDifferential,
    summaryFloatingDiscount,
    summaryReferenceMarket,
    summaryReferenceMonth,
    summaryReferenceYear,
    summaryReferenceIndex,
  ] = summaryWatch
  const summarySymbol = currencySymbol(summaryCurrency as string | null)

  function handleSubmit(values: ContractFormOutput) {
    const payload: ContractWriteRequest = { ...values }
    onSubmit(payload)
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-6"
        noValidate
      >
        <Card>
          <CardHeader>
            <CardTitle>{t("form.sections.info")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="contractNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.contractNumber")}</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("form.fields.clientName")}
                    <RequiredMark />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contractDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.contractDate")}</FormLabel>
                  <FormControl>
                    <DatePopover
                      value={field.value ? String(field.value).slice(0, 10) : ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contractType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.contractType")}</FormLabel>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("common.selectOption", { ns: "common" })}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTRACT_TYPE_VALUES.map((v) => (
                        <SelectItem key={v} value={v}>
                          {t(`options.contractTypes.${v}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("form.sections.product")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="productType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.productType")}</FormLabel>
                  <Select value={field.value ?? ""} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PRODUCT_TYPE_VALUES.map((v) => (
                        <SelectItem key={v} value={v}>
                          {t(`options.productTypes.${v}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cocoaGrade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.cocoaGrade")}</FormLabel>
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NONE}>—</SelectItem>
                      {COCOA_GRADE_VALUES.map((v) => (
                        <SelectItem key={v} value={v}>
                          {t(`options.cocoaGrades.${v}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("form.fields.quantity")}
                    <RequiredMark />
                  </FormLabel>
                  <FormControl>
                    <Input type="number" step="any" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="origin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.origin")}</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="certificates"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>{t("form.fields.certificates")}</FormLabel>
                  <div className="flex flex-wrap gap-3">
                    {CERTIFICATE_VALUES.map((value) => {
                      const checked = field.value?.includes(value) ?? false
                      return (
                        <label
                          key={value}
                          className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(state) => {
                              const next = new Set(field.value ?? [])
                              if (state === true) next.add(value)
                              else next.delete(value)
                              field.onChange(Array.from(next))
                            }}
                          />
                          {t(`options.certificates.${value}`)}
                        </label>
                      )
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("form.sections.delivery")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="incoterms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.incoterms")}</FormLabel>
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NONE}>—</SelectItem>
                      {INCOTERM_VALUES.map((v) => (
                        <SelectItem key={v} value={v}>
                          {t(`options.incoterms.${v}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentTerms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.paymentTerms")}</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="portOfLoading"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.portOfLoading")}</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="destinationPort"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.destinationPort")}</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contractDurationStart"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.contractDurationStart")}</FormLabel>
                  <FormControl>
                    <DatePopover
                      value={field.value ? String(field.value).slice(0, 10) : ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contractDurationEnd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.contractDurationEnd")}</FormLabel>
                  <FormControl>
                    <DatePopover
                      value={field.value ? String(field.value).slice(0, 10) : ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("form.sections.pricing")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="pricingMechanism"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("form.fields.pricingMechanism")}
                      <RequiredMark />
                    </FormLabel>
                    <Select
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t(
                              "form.fields.pricingMechanismPlaceholder",
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PRICING_MECHANISM_VALUES.map((v) => (
                          <SelectItem key={v} value={v}>
                            {t(`options.pricingMechanisms.${v}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("form.fields.currency")}
                      <RequiredMark />
                    </FormLabel>
                    <Select value={field.value ?? ""} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCY_VALUES.map((v) => (
                          <SelectItem key={v} value={v}>
                            {t(`options.currencies.${v}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {showFixedPrice ? (
              <FormField
                control={form.control}
                name="basePrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.fields.basePrice")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="any" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            {showDifferential || showPtbf ? (
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="differential"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.differential")}</FormLabel>
                      <FormControl>
                        <Input type="number" step="any" {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="countryDifferential"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.countryDifferential")}</FormLabel>
                      <FormControl>
                        <Input type="number" step="any" {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="referenceMarket"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.referenceMarket")}</FormLabel>
                      <Select
                        value={field.value ?? NONE}
                        onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>—</SelectItem>
                          {REFERENCE_MARKET_VALUES.map((v) => (
                            <SelectItem key={v} value={v}>
                              {t(`options.referenceMarkets.${v}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="referenceMonth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.referenceMonth")}</FormLabel>
                      <Select
                        value={field.value ?? NONE}
                        onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>—</SelectItem>
                          {MONTH_VALUES.map((v) => (
                            <SelectItem key={v} value={v}>
                              {t(`options.months.${v}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="referenceYear"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.referenceYear")}</FormLabel>
                      <Select
                        value={field.value === null || field.value === undefined || field.value === "" ? NONE : String(field.value)}
                        onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>—</SelectItem>
                          {REFERENCE_YEARS.map((y) => (
                            <SelectItem key={y} value={y}>
                              {y}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : null}

            {showFloating ? (
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="referenceIndex"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.referenceIndex")}</FormLabel>
                      <Select
                        value={field.value ?? NONE}
                        onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>—</SelectItem>
                          {REFERENCE_INDEX_VALUES.map((v) => (
                            <SelectItem key={v} value={v}>
                              {t(`options.referenceIndexes.${v}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="floatingDiscount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.floatingDiscount")}</FormLabel>
                      <FormControl>
                        <Input type="number" step="any" {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="calculationMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.calculationMethod")}</FormLabel>
                      <Select
                        value={field.value ?? NONE}
                        onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("common.selectOption", { ns: "common" })} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>—</SelectItem>
                          {CALCULATION_METHOD_VALUES.map((v) => (
                            <SelectItem key={v} value={v}>
                              {t(`options.calculationMethods.${v}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="fixingStartDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.fixingStartDate")}</FormLabel>
                      <FormControl>
                        <DatePopover
                          value={field.value ? String(field.value).slice(0, 10) : ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="fixingEndDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.fixingEndDate")}</FormLabel>
                      <FormControl>
                        <DatePopover
                          value={field.value ? String(field.value).slice(0, 10) : ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : null}

            <div className="rounded-lg border bg-muted/40 p-4">
              <h5 className="mb-4 text-sm font-medium text-foreground">
                {t("form.premiums.supplierPremium")}
              </h5>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="certificationPremiumPerKilo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("form.premiums.certificationLabel", {
                          currency: summarySymbol || "—",
                        })}
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={t("form.premiums.examplePlaceholder30")}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="agroforestryBonusPerKilo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("form.premiums.otherLabel", {
                          currency: summarySymbol || "—",
                        })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={t("form.premiums.examplePlaceholder25")}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <hr className="my-4 border-border" />
              <h5 className="mb-4 text-sm font-medium text-foreground">
                {t("form.premiums.localConversion")}
              </h5>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="localCertificationPremiumPerKilo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("form.premiums.certificationLabel", { currency: "CFA" })}
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={t("form.premiums.examplePlaceholder30")}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="localAgroforestryBonusPerKilo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("form.premiums.otherLabel", { currency: "CFA" })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={t("form.premiums.examplePlaceholder25")}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="adjustments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.adjustments")}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t("form.fields.adjustmentsPlaceholder")}
                      {...field}
                      value={(field.value ?? "") as string}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="rounded-lg border bg-muted/40 p-4">
              <h4 className="mb-3 text-sm font-medium text-foreground">
                {t("form.priceSummary.title")}
              </h4>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-md border bg-background p-3">
                  <div className="text-muted-foreground text-xs">
                    {t("form.priceSummary.pricingMethod")}
                  </div>
                  <div className="text-foreground text-lg font-semibold">
                    {pricingMechanism
                      ? t(`options.pricingMechanisms.${pricingMechanism}`)
                      : t("form.priceSummary.empty")}
                  </div>
                </div>
                {showFixedPrice ? (
                  <>
                    <div className="rounded-md border bg-background p-3">
                      <div className="text-muted-foreground text-xs">
                        {t("form.priceSummary.basePrice")}
                      </div>
                      <div className="text-foreground text-lg font-semibold">
                        {summaryBasePrice !== null && summaryBasePrice !== undefined && summaryBasePrice !== ""
                          ? `${summarySymbol} ${formatMoney(Number(summaryBasePrice))}/MT`
                          : t("form.priceSummary.empty")}
                      </div>
                    </div>
                    <div className="rounded-md border bg-background p-3">
                      <div className="text-muted-foreground text-xs">
                        {t("form.priceSummary.totalEstimate")}
                      </div>
                      <div className="text-foreground text-lg font-semibold">
                        {summaryQuantity && summaryBasePrice
                          ? `${summarySymbol} ${formatMoney(
                              Number(summaryQuantity) * Number(summaryBasePrice),
                            )}`
                          : t("form.priceSummary.empty")}
                      </div>
                    </div>
                  </>
                ) : null}
                {showDifferential || showPtbf ? (
                  <>
                    <div className="rounded-md border bg-background p-3">
                      <div className="text-muted-foreground text-xs">
                        {t("form.priceSummary.reference")}
                      </div>
                      <div className="text-foreground text-lg font-semibold">
                        {summaryReferenceMarket && summaryReferenceMonth && summaryReferenceYear
                          ? `${t(
                              `options.referenceMarkets.${summaryReferenceMarket}`,
                            )} ${t(
                              `options.months.${summaryReferenceMonth}`,
                            ).slice(0, 3)}. ${summaryReferenceYear}`
                          : t("form.priceSummary.empty")}
                      </div>
                    </div>
                    <div className="rounded-md border bg-background p-3">
                      <div className="text-muted-foreground text-xs">
                        {t("form.priceSummary.differential")}
                      </div>
                      <div className="text-foreground text-lg font-semibold">
                        {summaryDifferential !== null && summaryDifferential !== undefined && summaryDifferential !== ""
                          ? `${
                              String(summaryDifferential).startsWith("-") ? "" : "+"
                            }${summarySymbol} ${formatMoney(
                              Number(summaryDifferential),
                            )}/MT`
                          : t("form.priceSummary.empty")}
                      </div>
                    </div>
                  </>
                ) : null}
                {showFloating ? (
                  <>
                    <div className="rounded-md border bg-background p-3">
                      <div className="text-muted-foreground text-xs">
                        {t("form.priceSummary.referenceIndex")}
                      </div>
                      <div className="text-foreground text-lg font-semibold">
                        {summaryReferenceIndex
                          ? t(
                              `options.referenceIndexes.${summaryReferenceIndex}`,
                            )
                          : t("form.priceSummary.empty")}
                      </div>
                    </div>
                    <div className="rounded-md border bg-background p-3">
                      <div className="text-muted-foreground text-xs">
                        {t("form.priceSummary.premiumDiscount")}
                      </div>
                      <div className="text-foreground text-lg font-semibold">
                        {summaryFloatingDiscount !== null && summaryFloatingDiscount !== undefined && summaryFloatingDiscount !== ""
                          ? `${
                              String(summaryFloatingDiscount).startsWith("-") ? "" : "+"
                            }${summarySymbol} ${formatMoney(
                              Number(summaryFloatingDiscount),
                            )}/MT`
                          : t("form.priceSummary.empty")}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("form.sections.compliance")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <FormField
              control={form.control}
              name="onlyAcceptsEUDR"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border bg-card p-4 space-y-0">
                  <div className="flex-1 space-y-1">
                    <FormLabel className="text-sm font-medium">
                      {t("form.compliance.onlyAcceptsEUDR.label")}
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      {t("form.compliance.onlyAcceptsEUDR.description")}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex flex-row items-start justify-between gap-4 rounded-lg border bg-muted/40 p-4 opacity-70">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    {t("form.compliance.onlyAcceptsCertificates.label")}
                  </span>
                  <Badge variant="secondary">
                    {t("form.compliance.comingSoon")}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("form.compliance.onlyAcceptsCertificates.description")}
                </p>
              </div>
              <Switch checked={false} disabled />
            </div>

            <div className="flex flex-row items-start justify-between gap-4 rounded-lg border bg-muted/40 p-4 opacity-70">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    {t("form.compliance.onlyAcceptNoChildLabor.label")}
                  </span>
                  <Badge variant="secondary">
                    {t("form.compliance.comingSoon")}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("form.compliance.onlyAcceptNoChildLabor.description")}
                </p>
              </div>
              <Switch checked={false} disabled />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("details.notes")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.notes")}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} value={field.value ?? ""} />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="bg-background sticky bottom-0 z-10 flex justify-end gap-2 border-t py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            {t("actions.cancel", { ns: "common" })}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("actions.saving", { ns: "common" })}
              </>
            ) : contract ? (
              t("editContract")
            ) : (
              t("createContract")
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
